import React, { useEffect, useState, useCallback } from 'react';
import { userAPI, apiKeyAPI } from '../../services/api';
import { useAuth } from '../../AuthContext';
import './adminPage.css';

const ROLES = ['User', 'Admin'];

const AdminPage = () => {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [busyId, setBusyId] = useState(null);

    // Adding a user lives here because this is the only place it can: /auth/register
    // requires Admin now that self-registration is closed, so without this there is
    // no way to onboard anyone at all.
    const [showAdd, setShowAdd] = useState(false);
    const [newUser, setNewUser] = useState({ username: '', email: '', password: '' });
    const [adding, setAdding] = useState(false);
    const [addNote, setAddNote] = useState('');

    // ---- API keys (machine credentials for integrations) ----
    const [apiKeys, setApiKeys] = useState([]);
    const [keysLoading, setKeysLoading] = useState(true);
    const [keyName, setKeyName] = useState('');
    const [mintingKey, setMintingKey] = useState(false);
    const [keyError, setKeyError] = useState('');

    // The key itself, held in state after minting. This is the ONLY time it can be
    // read — it is stored hashed, so there is no way to show it again. It stays on
    // screen until explicitly dismissed rather than clearing on the next render,
    // because a key that vanishes before someone copies it is a key that has to be
    // revoked and re-minted.
    const [mintedKey, setMintedKey] = useState(null);
    const [copied, setCopied] = useState(false);

    // Who the key writes as. 'new' creates a service account named after the key; any
    // other value is an existing user's id.
    //
    // This is not cosmetic: notes, tasks and documents store their author as a
    // non-nullable link to a user, so a key with no identity gets a foreign-key error
    // on all three while jobs, contacts and custom fields — which record no author —
    // succeed. That split is confusing enough that the default is to always create one.
    const [keyIdentity, setKeyIdentity] = useState('new');
    const [linkBusyId, setLinkBusyId] = useState(null);

    const loadApiKeys = useCallback(async () => {
        try {
            setKeysLoading(true);
            const res = await apiKeyAPI.getAll();
            setApiKeys(res.data || []);
        } catch (err) {
            console.error('Error loading API keys:', err);
            setKeyError('Could not load API keys.');
        } finally {
            setKeysLoading(false);
        }
    }, []);

    useEffect(() => {
        loadApiKeys();
    }, [loadApiKeys]);

    const handleMintKey = async (e) => {
        e.preventDefault();
        const name = keyName.trim();
        if (!name) {
            setKeyError('Give the key a name — a list of unnamed keys cannot be managed.');
            return;
        }
        setMintingKey(true);
        setKeyError('');
        try {
            const res = await apiKeyAPI.create(
                name,
                keyIdentity === 'new'
                    ? { createServiceUser: true }
                    : { createServiceUser: false, linkedUserId: Number(keyIdentity) }
            );
            setMintedKey(res.data);
            setCopied(false);
            setKeyName('');
            setKeyIdentity('new');
            await loadUsers();
            await loadApiKeys();
        } catch (err) {
            const data = err.response?.data;
            setKeyError(typeof data === 'string' ? data : data?.message || 'Could not create the key.');
        } finally {
            setMintingKey(false);
        }
    };

    const handleRevokeKey = async (key) => {
        // Spelled out rather than a generic "are you sure": revoking breaks whatever
        // is using it, immediately, and the name is the only clue to what that is.
        if (!window.confirm(
            `Revoke "${key.name}"?\n\nAnything using this key stops working immediately. `
            + `This cannot be undone — you would have to issue a new key and update the integration.`
        )) return;

        try {
            await apiKeyAPI.revoke(key.id);
            await loadApiKeys();
        } catch (err) {
            setKeyError('Could not revoke that key.');
        }
    };


    // Repointing a key's identity. Unlinking is warned about rather than blocked: it
    // is occasionally the right call, but it silently breaks the integration's notes
    // and documents while leaving everything else working, which is the hardest kind
    // of failure to attribute later.
    const handleSetKeyIdentity = async (key, value) => {
        // '__new' mints a service account named after the key. The only route for a key
        // issued before identities existed: nothing in the user list belongs to it.
        if (value === '__new') {
            setLinkBusyId(key.id);
            setKeyError('');
            try {
                await apiKeyAPI.createServiceUserFor(key.id);
                await loadApiKeys();
                await loadUsers();
            } catch (err) {
                const data = err.response?.data;
                setKeyError(typeof data === 'string' ? data : data?.message || 'Could not create the account.');
            } finally {
                setLinkBusyId(null);
            }
            return;
        }

        const userId = value === '' ? null : Number(value);

        if (userId === null && !window.confirm(
            `Unlink "${key.name}" from ${key.linkedUserName}?\n\n`
            + `Notes, tasks and documents written through this key will start failing. `
            + `Jobs, contacts and custom fields will keep working, which makes this easy to miss.`
        )) return;

        setLinkBusyId(key.id);
        setKeyError('');
        try {
            await apiKeyAPI.setLinkedUser(key.id, userId);
            await loadApiKeys();
        } catch (err) {
            const data = err.response?.data;
            setKeyError(typeof data === 'string' ? data : data?.message || 'Could not change the identity.');
        } finally {
            setLinkBusyId(null);
        }
    };
    const copyKey = async () => {
        try {
            await navigator.clipboard.writeText(mintedKey.key);
            setCopied(true);
        } catch {
            // Clipboard access can be refused (insecure context, permissions). The key
            // is selectable text on screen either way, so this is a convenience only.
            setCopied(false);
        }
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        setAdding(true);
        setError('');
        setAddNote('');
        try {
            await userAPI.create({
                username: newUser.username.trim(),
                email: newUser.email.trim(),
                password: newUser.password,
            });
            // Always created as "User" — the server ignores any role sent here. Promote
            // with the Role dropdown in the table if they need it.
            setAddNote(`${newUser.username.trim()} added as a User. Use the Role column to make them an admin.`);
            setNewUser({ username: '', email: '', password: '' });
            setShowAdd(false);
            await loadUsers();
        } catch (err) {
            const data = err.response?.data;
            setError(typeof data === 'string' ? data : data?.message || data?.title || 'Failed to add the user.');
        } finally {
            setAdding(false);
        }
    };

    const loadUsers = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            // Service accounts included: this is the one screen where an integration's
            // author row is the subject rather than noise in a picker.
            const res = await userAPI.getAll(true);
            setUsers(res.data);
        } catch (err) {
            console.error('Error loading users:', err);
            setError('Failed to load users.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const handleRoleChange = async (id, newRole) => {
        const target = users.find(u => u.id === id);
        if (!target) return;
        if (target.role === newRole) return;

        const confirmMsg = newRole === 'Admin'
            ? `Promote "${target.username}" to Admin? They will gain full administrative access.`
            : `Demote "${target.username}" to User?`;
        if (!window.confirm(confirmMsg)) return;

        setBusyId(id);
        setError('');
        try {
            await userAPI.updateRole(id, newRole);
            await loadUsers();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update role.');
        } finally {
            setBusyId(null);
        }
    };

    const handleDelete = async (id) => {
        const target = users.find(u => u.id === id);
        if (!target) return;
        if (!window.confirm(`Delete user "${target.username}"? This can't be undone.`)) return;

        setBusyId(id);
        setError('');
        try {
            await userAPI.delete(id);
            await loadUsers();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete user.');
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="task-dashboard">
            <div className="dashboard-header">
                <div className="header-left">
                    <h2 className="dashboard-title">Admin · Users</h2>
                    <span className="task-count">({users.length})</span>
                </div>
                <div className="header-actions">
                    <button type="button" className="btn-primary" onClick={() => { setShowAdd(v => !v); setAddNote(''); }}>
                        {showAdd ? 'Cancel' : '+ Add User'}
                    </button>
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}
            {addNote && <div className="success-message" style={{ color: 'var(--accent-green)' }}>{addNote}</div>}

            {showAdd && (
                <form onSubmit={handleAdd} className="admin-form-row">
                    <div className="admin-form-field">
                        <label className="form-label" htmlFor="new-user-username">Username</label>
                        <input
                            id="new-user-username"
                            type="text"
                            className="form-input"
                            value={newUser.username}
                            onChange={(e) => setNewUser(p => ({ ...p, username: e.target.value }))}
                            required
                            minLength={3}
                            maxLength={50}
                        />
                    </div>
                    <div className="admin-form-field">
                        <label className="form-label" htmlFor="new-user-email">Email</label>
                        <input
                            id="new-user-email"
                            type="email"
                            className="form-input"
                            value={newUser.email}
                            onChange={(e) => setNewUser(p => ({ ...p, email: e.target.value }))}
                            required
                            maxLength={100}
                        />
                    </div>
                    <div className="admin-form-field">
                        <label className="form-label" htmlFor="new-user-password">Temporary password</label>
                        <input
                            id="new-user-password"
                            type="text"
                            className="form-input"
                            value={newUser.password}
                            onChange={(e) => setNewUser(p => ({ ...p, password: e.target.value }))}
                            required
                            minLength={6}
                            maxLength={100}
                        />
                    </div>
                    <button type="submit" className="btn-primary" disabled={adding}>
                        {adding ? 'Adding…' : 'Add'}
                    </button>
                    {/* Shown rather than masked: you have to read it out to the person.
                        They change it themselves from the login screen afterwards. */}
                    <p className="form-hint admin-form-hint">
                        The password is visible so you can read it to them — they change it
                        themselves via Forgot Password. New accounts are created as Users; use
                        the role dropdown below to promote one.
                    </p>
                </form>
            )}

            {loading ? (
                <div className="loading">Loading users...</div>
            ) : users.length === 0 ? (
                <div className="empty-state">
                    <h3>No users found</h3>
                </div>
            ) : (
                <div className="tasks-table-container">
                    <table className="tasks-table">
                        <thead>
                            <tr>
                                <th>Username</th>
                                <th>Email</th>
                                <th>Role</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(u => {
                                const isSelf = u.id === currentUser?.id;
                                const busy = busyId === u.id;
                                return (
                                    <tr key={u.id} className="task-row">
                                        <td>
                                            {u.username}
                                            {isSelf && (
                                                <span style={{ marginLeft: '6px', fontSize: '11px', color: 'var(--text-muted, #57606a)', textTransform: 'uppercase' }}>
                                                    you
                                                </span>
                                            )}
                                            {u.isServiceAccount && (
                                                <span className="service-account-badge" title="A machine account an API key writes as. Not a person; hidden from assignee pickers.">
                                                    integration
                                                </span>
                                            )}
                                        </td>
                                        <td>{u.email}</td>
                                        <td>
                                            <select
                                                value={u.role}
                                                onChange={(e) => handleRoleChange(u.id, e.target.value)}
                                                disabled={busy || isSelf}
                                                title={isSelf ? "You can't change your own role" : ''}
                                                className="form-input"
                                                style={{ minWidth: '110px' }}
                                            >
                                                {ROLES.map(r => (
                                                    <option key={r} value={r}>{r}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className="due-date">
                                            {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}
                                        </td>
                                        <td className="task-actions">
                                            <button
                                                className="action-btn btn-complete"
                                                onClick={() => handleDelete(u.id)}
                                                disabled={busy || isSelf}
                                                title={isSelf ? "You can't delete your own account" : 'Delete user'}
                                                style={{
                                                    background: '#dc3545',
                                                    opacity: (busy || isSelf) ? 0.5 : 1,
                                                    cursor: (busy || isSelf) ? 'not-allowed' : 'pointer',
                                                }}
                                            >
                                                <i className="fas fa-trash-alt"></i>
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* API keys — the machine half of "who can get into this system", which is
                why it sits under Users rather than on a page of its own. */}
            <div className="dashboard-header" style={{ marginTop: '32px' }}>
                <div className="header-left">
                    <h2 className="dashboard-title">API Keys</h2>
                    <span className="task-count">({apiKeys.filter(k => k.isActive).length} active)</span>
                </div>
            </div>

            <p className="form-hint" style={{ marginBottom: '12px' }}>
                For integrations that call Artemis without being a person — Ground Control,
                for example. A key can read and write jobs and contacts, but cannot delete
                them, create users, or issue keys.
            </p>

            {keyError && <div className="error-message">{keyError}</div>}

            {/* Shown once. Deliberately loud and deliberately not auto-dismissing: the
                key cannot be retrieved later, so losing it here means revoking and
                re-issuing. */}
            {mintedKey && (
                <div className="api-key-reveal">
                    <div className="api-key-reveal-head">
                        <strong>Copy this now — it cannot be shown again</strong>
                        <button type="button" onClick={() => setMintedKey(null)}>Done</button>
                    </div>
                    <div className="api-key-reveal-name">{mintedKey.name}</div>
                    <code className="api-key-reveal-value">{mintedKey.key}</code>
                    <div className="api-key-reveal-actions">
                        <button type="button" className="btn-primary" onClick={copyKey}>
                            {copied ? 'Copied' : 'Copy key'}
                        </button>
                        <span className="form-hint">
                            Send it as an <code>X-Api-Key</code> header to{' '}
                            <code>https://api.artemis.proroofersinc.com</code>
                        </span>
                    </div>
                </div>
            )}

            {/* Its own grid rather than .form-row, which is a global 1fr 1fr 1fr from the
                project modal — it gave the button a full third of the width and, because
                the two fields' hints are different heights, pushed the controls out of
                line with each other. Hints live on their own row here so the label and
                input of every column always share a baseline. */}
            <form onSubmit={handleMintKey} className="admin-form-row">
                <div className="admin-form-field">
                    <label className="form-label" htmlFor="new-key-name">New key name</label>
                    <input
                        id="new-key-name"
                        type="text"
                        className="form-input"
                        value={keyName}
                        onChange={(e) => setKeyName(e.target.value)}
                        placeholder="e.g. ground-control"
                        maxLength={100}
                    />
                </div>
                <div className="admin-form-field">
                    <label className="form-label" htmlFor="new-key-identity">Writes as</label>
                    <select
                        id="new-key-identity"
                        className="form-input"
                        value={keyIdentity}
                        onChange={(e) => setKeyIdentity(e.target.value)}
                    >
                        <option value="new">New service account</option>
                        {users.map((u) => (
                            <option key={u.id} value={String(u.id)}>
                                {u.username}{u.isServiceAccount ? ' (integration)' : ''}
                            </option>
                        ))}
                    </select>
                </div>
                <button type="submit" className="btn-primary" disabled={mintingKey}>
                    {mintingKey ? 'Creating…' : 'Create key'}
                </button>
                <p className="form-hint admin-form-hint">
                    Name it after whatever will use it — the name is the audit trail. Notes,
                    tasks and documents record an author, so leave <strong>Writes as</strong> on
                    a new service account unless this key should write as an existing one.
                </p>
            </form>

            {keysLoading ? (
                <div className="loading">Loading keys…</div>
            ) : apiKeys.length === 0 ? (
                <div className="empty-state"><h3>No API keys</h3></div>
            ) : (
                <div className="tasks-table-container">
                    <table className="tasks-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Key</th>
                                <th>Writes as</th>
                                <th>Created</th>
                                <th>Last used</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {apiKeys.map((k) => (
                                <tr key={k.id} style={k.isActive ? undefined : { opacity: 0.55 }}>
                                    <td>{k.name}</td>
                                    {/* The stored prefix, which is enough to match a key in a
                                        config file to this row without revealing anything usable. */}
                                    <td><code>{k.prefix}…</code></td>
                                    {/* The author of record for anything this key writes.
                                        Shown as a live control rather than text because the
                                        key minted before identities existed needs repointing
                                        here, not re-issuing. */}
                                    <td>
                                        {k.isActive ? (
                                            <select
                                                className="form-input api-key-identity-select"
                                                value={k.linkedUserId ? String(k.linkedUserId) : ''}
                                                disabled={linkBusyId === k.id}
                                                onChange={(e) => handleSetKeyIdentity(k, e.target.value)}
                                            >
                                                <option value="">— none —</option>
                                                <option value="__new">+ New service account</option>
                                                {users.map((u) => (
                                                    <option key={u.id} value={String(u.id)}>
                                                        {u.username}{u.isServiceAccount ? ' (integration)' : ''}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            k.linkedUserName || '—'
                                        )}
                                    </td>
                                    <td>{new Date(k.createdAt).toLocaleDateString()}</td>
                                    {/* "Never" is the useful answer here: a key that has never
                                        been used is one you can revoke without breaking anything. */}
                                    <td>{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : 'Never'}</td>
                                    <td>{k.isActive ? 'Active' : 'Revoked'}</td>
                                    <td>
                                        {k.isActive && (
                                            <button
                                                className="action-btn btn-delete"
                                                onClick={() => handleRevokeKey(k)}
                                                title="Revoke key"
                                            >
                                                Revoke
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AdminPage;
